import { logSpace, linearRegression, computeSSE, computeRSquared } from '../utils/math';
import { computePoissonLogL } from './diagnostics';
import { computeExpectedCurveCounts, computeIncrementalMetrics } from './curveUtils';
import { filterDiscoveryCounts } from '../series/discoverySeries';

export const logisticCurve = (params, x) => params.L / (1 + Math.exp(-params.k * (x - params.x0)));

export const fitLogistic = (points, options = {}) => {
  if (!points || points.length < 3) return null;
  const xs = points.map(point => point.x);
  const ys = points.map(point => point.y);
  const maxY = Math.max(...ys);
  if (!Number.isFinite(maxY) || maxY <= 0) return null;

  const {
    discoveryCounts,
    modelStartYear,
    fitMethod = 'cumulative',
  } = options;

  const minL = maxY * 1.01;
  const maxL = maxY * 10;
  const candidates = logSpace(minL, maxL, 36);
  let best = null;

  const evaluate = (L) => {
    const xVals = [];
    const zVals = [];
    for (let i = 0; i < points.length; i += 1) {
      const y = points[i].y;
      const x = points[i].x;
      if (!(y > 0 && y < L)) continue;
      const ratio = L / y - 1;
      if (ratio <= 0) continue;
      const z = Math.log(ratio);
      if (!Number.isFinite(z)) continue;
      xVals.push(x);
      zVals.push(z);
    }

    if (xVals.length < 3) return;
    const lr = linearRegression(xVals, zVals);
    if (!lr || lr.slope >= 0) return;
    const k = -lr.slope;
    const x0 = lr.intercept / k;
    if (!Number.isFinite(k) || !Number.isFinite(x0)) return;

    const predicted = xs.map(x => logisticCurve({ L, k, x0 }, x));
    const sse = computeSSE(ys, predicted);
    const rSquared = computeRSquared(ys, predicted);

    let logL = null;
    if (fitMethod === 'poisson' && discoveryCounts && Number.isFinite(modelStartYear)) {
      const expected = computeExpectedCurveCounts(logisticCurve, { L, k, x0 }, discoveryCounts, modelStartYear);
      const observed = filterDiscoveryCounts(discoveryCounts, modelStartYear);
      logL = expected ? computePoissonLogL(observed, expected) : null;
      if (!Number.isFinite(logL)) return;
    }

    if (fitMethod === 'poisson') {
      if (!best || logL > best.logL) {
        best = { params: { L, k, x0 }, rSquared, sse, logL };
      }
    } else if (!best || sse < best.sse) {
      best = { params: { L, k, x0 }, rSquared, sse };
    }
  };

  candidates.forEach(evaluate);
  if (!best) return null;

  const refineMin = Math.max(minL, best.params.L / 1.5);
  const refineMax = best.params.L * 1.5;
  const refinedCandidates = logSpace(refineMin, refineMax, 24);
  refinedCandidates.forEach(evaluate);

  const result = { ...best, fitMethod };
  if (discoveryCounts && Number.isFinite(modelStartYear)) {
    const expected = computeExpectedCurveCounts(logisticCurve, result.params, discoveryCounts, modelStartYear);
    const observed = filterDiscoveryCounts(discoveryCounts, modelStartYear);
    const metrics = expected ? computeIncrementalMetrics(observed, expected) : null;
    if (metrics && Number.isFinite(metrics.rSquared)) {
      result.rSquaredCumulative = result.rSquared;
      result.rSquared = metrics.rSquared;
      result.incrementalSse = metrics.sse;
    }
  }

  return result;
};
